import { viteVar } from "./env";

export type Offer = {
  id: "local-trust-pack" | "site-leak-report";
  label: string;
  name: string;
  cardName: string;
  price: string;
  outcome: string;
  benefits: readonly [string, string, string];
  idealFor: string;
  cta: string;
  pagePath: string;
  summary: string;
  who: readonly string[];
  ships: readonly string[];
  needs: readonly string[];
  paymentUrl: string;
  intakeUrl: string;
  postPaymentPath: string;
};

export const seo = {
  title: "Foxmark - Monthly Trust and Website Reports for Local Businesses",
  description:
    "Foxmark helps local businesses find the gaps that cost calls, bookings, and trust. Monthly review, profile, and website reports starting at $149/month."
} as const;

export const heroCopy = {
  eyebrow: "Monthly visibility and trust reports for local businesses",
  headline: "Monthly reputation and website reports for local businesses",
  tagline: "Find what customers notice before they call someone else",
  subhead:
    "Foxmark helps local businesses spot the gaps in their public presence: unanswered reviews, weak profile activity, confusing website paths, missing trust signals, and simple fixes that can help more customers choose you.",
  secondaryLine:
    "No bloated retainer. No vague strategy deck. Just a clear monthly pack of practical work.",
  primaryCta: "Choose Your Monthly Pack",
  secondaryCta: "See What Foxmark Checks",
  proofLine: "Flat $149/month · No contracts · Cancel anytime"
} as const;

export const sampleReport = {
  title: "Local Trust Snapshot",
  business: "Hillside Dental Studio",
  date: "June 2026",
  metrics: [
    { value: "4.6", label: "Average rating" },
    { value: "12/12", label: "Reviews answered" },
    { value: "4", label: "Profile posts prepared" },
    { value: "3", label: "Priority fixes found" }
  ],
  language: ["gentle with kids", "easy scheduling", "clear explanation", "friendly front desk"],
  caption: "Sample monthly Foxmark report"
} as const;

export const servicesIntro = {
  headline: "A sharper look at the places customers judge you first",
  body: [
    "Before most people call, book, or request a quote, they check the same few things: your reviews, your business profile, your website, your photos, your service details, and how easy it is to take the next step.",
    "Foxmark reviews those customer-facing signals every month and turns them into useful deliverables your business can actually use."
  ]
} as const;

export const featureStrip = [
  {
    title: "Review reply drafted",
    body:
      "Thank you, Maria. We're glad the team made the visit feel simple and comfortable. We've also updated our booking page to make scheduling easier."
  },
  {
    title: "Business profile post prepared",
    body: "A customer win, seasonal service, or local reminder turned into a short post for your profile."
  },
  {
    title: "Website leak identified",
    body: "Mobile visitors can't find the phone number fast enough. Move the call button above the fold."
  }
] as const;

export const offers: Offer[] = [
  {
    id: "local-trust-pack",
    label: "Foxmark",
    name: "Foxmark Local Trust Pack",
    cardName: "Local Trust Pack",
    price: "$149/mo",
    outcome:
      "Turn your existing customer reviews into a stronger, more active local presence.",
    benefits: [
      "Review responses written in your brand voice",
      "Four Google Business Profile posts each month",
      "Monthly trust insights and recommended next steps"
    ],
    idealFor:
      "Local businesses receiving reviews but not consistently using them.",
    cta: "Start building trust",
    pagePath: "/google-review-management",
    summary:
      "A monthly review and business profile pack that helps your company look active, responsive, and trustworthy when local customers are deciding who to call.",
    who: [
      "Local businesses where trust drives the sale: dentists, med spas, home service companies, salons, restaurants, wellness clinics, repair shops, contractors, and professional services.",
      "If customers check your reviews before they contact you, this is the pack to start with."
    ],
    ships: [
      "Every new review answered in your brand voice, drafted for approval or publishing.",
      "Four business profile posts prepared for the month.",
      "A one-page Local Trust Report showing rating trends, repeated customer themes, and profile opportunities.",
      "Plain-English recommendations for what to update next on your profile, website, or customer experience."
    ],
    needs: [
      "Your Google Business Profile link.",
      "Your business name.",
      "A short description of your ideal customer.",
      "Any preferred tone for review replies."
    ],
    paymentUrl: viteVar("VITE_LOCAL_TRUST_PAYMENT_URL", "#start"),
    intakeUrl: viteVar("VITE_LOCAL_TRUST_INTAKE_URL", "#start"),
    postPaymentPath: "/local-trust-intake"
  },
  {
    id: "site-leak-report",
    label: "Foxmark",
    name: "Foxmark Site Leak Report",
    cardName: "Site Leak Report",
    price: "$149/mo",
    outcome:
      "Find and prioritize the website problems most likely costing you leads.",
    benefits: [
      "Ranked list of your most important conversion leaks",
      "Plain-English explanations and specific fixes",
      "A focused 30-day action plan"
    ],
    idealFor:
      "Businesses getting traffic but not enough calls, bookings, or form submissions.",
    cta: "Find my website leaks",
    pagePath: "/website-audit-service",
    summary:
      "A monthly website review that finds the trust, clarity, content, and conversion issues that may be costing you calls, bookings, form fills, or quote requests.",
    who: [
      "Business owners who know people visit the website, but not enough of them take action.",
      "This is for companies that want a clear list of what to fix without paying for a full agency audit every month."
    ],
    ships: [
      "A ranked list of the website leaks most likely to cost you leads.",
      "Plain-English explanations of each issue.",
      "Specific fix recommendations your team, web person, or agency can act on.",
      "A 30-day priority list focused on calls, forms, bookings, local visibility, trust signals, and mobile usability."
    ],
    needs: [
      "Your website URL.",
      "Your service area.",
      "The main action you want visitors to take.",
      "Any key services, locations, or offers you want prioritized.",
      "No website login required."
    ],
    paymentUrl: viteVar("VITE_SITE_LEAK_PAYMENT_URL", "#start"),
    intakeUrl: viteVar("VITE_SITE_LEAK_INTAKE_URL", "#start"),
    postPaymentPath: "/site-leak-intake"
  }
];

export const comparison = {
  headline: "Which Foxmark pack should you choose?",
  options: [
    "Choose Local Trust Pack if your reviews and business profile are active but inconsistent.",
    "Choose Site Leak Report if your website gets visitors but does not generate enough calls, bookings, or forms.",
    "Choose both if you want your public trust signals and website conversion issues reviewed every month."
  ],
  cta: "Start With One Pack"
} as const;

export const whyThisWorks = {
  headline: "Most local businesses lose leads in plain sight",
  points: [
    "The problem is not always a broken website or a missing marketing campaign. Sometimes it is simpler than that.",
    "A slow path to the phone number. A review that never got answered. A service page that does not explain enough. A business profile that looks inactive.",
    "Foxmark is built to spot those gaps and turn them into small, practical fixes every month."
  ]
} as const;

export const processSteps = [
  {
    number: "01",
    title: "Subscribe and complete intake",
    body: "Choose your pack, subscribe through Stripe, and complete a short intake form. It takes about five minutes."
  },
  {
    number: "02",
    title: "Foxmark reviews your public presence",
    body: "Your reviews, profile, website, service details, and customer-facing signals are reviewed for missed opportunities."
  },
  {
    number: "03",
    title: "Your monthly pack lands in your inbox",
    body: "You receive a clear monthly deliverable with drafts, findings, and next steps. Use it yourself, send it to your web person, or hand it to your team."
  }
] as const;

export const firstMonth = {
  headline: "Useful work without the agency runaround",
  body:
    "Your first month is designed to give you something practical quickly. No drawn-out onboarding process. No account manager handoff. No waiting for a quarterly strategy session.",
  deliverables: [
    "A completed intake review.",
    "Your first monthly Foxmark report.",
    "Clear recommendations written in plain English.",
    "Drafts or fixes you can use immediately.",
    "A simple next-step priority list for the following month."
  ]
} as const;

export const finalCta = {
  headline: "Stop letting easy trust and website gaps cost you leads",
  body:
    "Choose the monthly pack that matches the problem you want solved first. Start with one. Add the other later if you want both your local trust signals and website conversion issues reviewed every month.",
  microcopy: "$149/month · Cancel anytime · No long-term contract"
} as const;
